export interface DnsMxRecord {
  host: string;
  pref: number;
}

export interface DnsHttpInfo {
  url?: string;
  status_code?: number;
  wpx?: string;
  x_edge_location?: string;
  headers?: { [key: string]: string };
  error?: string;
}

export interface DnsPlainStep {
  title: string;
  detail: string;
  status: 'ok' | 'warn' | 'info' | 'error' | string;
}

export interface DnsNSMap {
  hostname: string;
  listed_nameservers: string[];
  authority_zone?: string;
  authority_type?: string;
  authority_nameservers: string[];
  authoritative_servers: string[];
  public_a_records: string[];
  verified_a_records: string[];
  a_records_match?: boolean | null;
  has_own_nameservers: boolean;
  summary: string;
  steps: DnsPlainStep[];
  error?: string;
}

export interface DnsCheckResult {
  domain: string;
  nameservers: string[];
  provider: string;
  a_records: string[];
  mx_records: DnsMxRecord[];
  http: DnsHttpInfo;
  ns_map: DnsNSMap;
  errors: string[];
}

export interface DnsServerCheckResult {
  hostname: string;
  ns_map: DnsNSMap;
  errors: string[];
}

export interface DnsCheckerRepositoryInterface {
  checkDomain(domain: string): Promise<DnsCheckResult>;
  checkServer(hostname: string): Promise<DnsServerCheckResult>;
}

const DEFAULT_BASE_URL = 'https://checker.fnxexp.dev';
const REQUEST_TIMEOUT_MS = 4500;

export default class DnsCheckerRepository implements DnsCheckerRepositoryInterface {
  private readonly baseURL: string;
  private readonly domainCache = new Map<string, DnsCheckResult>();
  private readonly serverCache = new Map<string, DnsServerCheckResult>();

  constructor(baseURL: string = process.env.VUE_APP_DNS_CHECKER_URL || DEFAULT_BASE_URL) {
    this.baseURL = baseURL.replace(/\/$/, '');
  }

  public async checkDomain(domain: string): Promise<DnsCheckResult> {
    const key = domain.trim().toLowerCase();
    const cached = this.domainCache.get(key);
    if (cached) {
      // eslint-disable-next-line no-console
      console.info('[dns-checker] domain cache hit', key);
      return cached;
    }

    const result = await this.fetchJSON<DnsCheckResult>(
      `${this.baseURL}/check?domain=${encodeURIComponent(key)}`,
      key,
    );
    this.domainCache.set(key, result);
    return result;
  }

  public async checkServer(hostname: string): Promise<DnsServerCheckResult> {
    const key = hostname.trim().toLowerCase();
    const cached = this.serverCache.get(key);
    if (cached) {
      // eslint-disable-next-line no-console
      console.info('[dns-checker] server cache hit', key);
      return cached;
    }

    const result = await this.fetchJSON<DnsServerCheckResult>(
      `${this.baseURL}/check-server?hostname=${encodeURIComponent(key)}`,
      key,
    );
    this.serverCache.set(key, result);
    return result;
  }

  private async fetchJSON<T>(url: string, label: string): Promise<T> {
    // eslint-disable-next-line no-console
    console.info('[dns-checker] fetch start', url);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        const body = await response.text();
        // eslint-disable-next-line no-console
        console.warn('[dns-checker] fetch non-ok', label, response.status, body);
        throw new Error(body || `DNS check failed (${response.status})`);
      }

      const result = await response.json() as T;
      // eslint-disable-next-line no-console
      console.info('[dns-checker] fetch ok', label);
      return result;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // eslint-disable-next-line no-console
        console.warn('[dns-checker] fetch timeout after', REQUEST_TIMEOUT_MS, 'ms', label);
        throw new Error(`DNS check timed out after ${REQUEST_TIMEOUT_MS}ms`);
      }
      // eslint-disable-next-line no-console
      console.warn('[dns-checker] fetch error', label, err);
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }
}
